// The one way the campaign screens ask "are you sure": a dialog that says what
// will happen, asks for a reason where the API records one, and repeats the
// action's own word on its button (DESIGN.md "Szövegírás"). It replaces the
// browser's `window.prompt`, which cannot say what happens next, cannot hold a
// longer reason, and cannot show the API's refusal without losing the text.
//
// The dialog stays open until the action has succeeded; a refusal is shown in
// it, under the reason the person typed, so nothing has to be typed twice.

import { useEffect, useId, useState } from "react";
import type { ReactNode } from "react";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ErrorState } from "@/components/emc/states";

const REASON_MAX = 500;

export interface ActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** One or two sentences: what happens, and what does not. */
  description?: ReactNode;
  /** More to read before deciding — a summary, a list of countries. */
  children?: ReactNode;
  /** Says exactly what will happen: "Kiküldés leállítása". */
  confirmLabel: ReactNode;
  /** Brick for stopping and giving up; pine for everything else. */
  tone?: "default" | "danger";
  /** Ask for a reason (the API writes it on the event). Required when given;
   * `max` is the API's own limit on it (500 when not said). */
  reason?: { label: string; placeholder?: string; max?: number };
  /** Runs the action. Throw to keep the dialog open with the message. */
  onConfirm: (reason: string) => Promise<void>;
}

function messageOf(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) return error.message;
  return "Ismeretlen hiba.";
}

export function ActionDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel,
  tone = "default",
  reason,
  onConfirm,
}: ActionDialogProps) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasonId = useId();

  // A dialog opened again starts clean: the last reason was for the last
  // decision.
  useEffect(() => {
    if (open) {
      setText("");
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const missingReason = reason !== undefined && text.trim() === "";

  async function confirm() {
    if (missingReason || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(text.trim());
      onOpenChange(false);
    } catch (raised: unknown) {
      setError(messageOf(raised));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        {reason && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={reasonId}>{reason.label}</Label>
            <Textarea
              id={reasonId}
              value={text}
              maxLength={reason.max ?? REASON_MAX}
              placeholder={reason.placeholder}
              onChange={(event) => setText(event.target.value)}
              aria-invalid={error !== null && missingReason ? true : undefined}
              autoFocus
            />
            <p className="text-label text-muted-foreground">
              A naplóba kerül a neveddel együtt. Legfeljebb {reason.max ?? REASON_MAX} karakter.
            </p>
          </div>
        )}
        {error !== null && <ErrorState message={error} />}
        <DialogFooter>
          <Button variant="outline" type="button" disabled={busy} onClick={() => onOpenChange(false)}>
            Mégse
          </Button>
          <Button
            type="button"
            variant={tone === "danger" ? "destructive" : "default"}
            disabled={busy || missingReason}
            onClick={() => void confirm()}
          >
            {busy ? "Folyamatban…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
